import type { ContractListItem, ContractState, ContractType, Lookups, Me } from '@ft/shared';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useNewShortcut } from '../components/CommandPalette';
import { ContractForm } from '../components/ContractForm';
import { SavedViews } from '../components/SavedViews';
import { Loading, useLoad, useToast } from '../components/ui';
import { useI18n, type T } from '../i18n';
import { canEdit as canEditArea, getAreaName } from '../lib/area';
import { custColor, dfmt, eur, pct } from '../lib/format';
import { useRememberedFilter } from '../lib/remember';

const TONE: Record<ContractState, string> = { noBudget: 'neu', ok: 'pos', nearlyUsed: 'crit', exceeded: 'neg' };

export function ContractStatePill({ c, t }: { c: Pick<ContractListItem, 'state' | 'endsInDays'>; t: T }) {
  return (
    <>
      <span className={`pill ${TONE[c.state]}`}>{t(`ct.state.${c.state}`)}</span>
      {c.endsInDays !== null && <span className={`pill ${c.endsInDays < 0 ? 'neu' : c.endsInDays <= 30 ? 'crit' : 'info'}`} style={{ marginLeft: 4 }}>{c.endsInDays < 0 ? t('ct.expired') : t('ct.endsIn', { n: c.endsInDays })}</span>}
    </>
  );
}

/** Bestellt im Verhältnis zum Budget; Strich = 100 %. */
export function BudgetBar({ ordered, budget }: { ordered: number; budget: number | null }) {
  if (!budget) return <span className="muted">–</span>;
  const u = ordered / budget;
  const col = u > 1 ? 'var(--neg)' : u >= 0.9 ? 'var(--crit)' : 'var(--accent)';
  return (
    <div className="use">
      <div className="meter"><i style={{ width: `${Math.min(100, u * 100)}%`, background: col }} /></div>
      <span className="num">{pct(u)}</span>
    </div>
  );
}

/** Hauptverträge (Rahmenverträge): Budget, Projekte, was durch Bestellungen schon verbraucht ist, Ablage. */
export function ContractsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('contracts');
  const { data, error } = useLoad<ContractListItem[]>('/api/contracts');
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const [creating, setCreating] = useState(false);
  const [showFree, setShowFree] = useState(false);
  const [view, setView] = useState<'cards' | 'table'>(() => { try { return localStorage.getItem('ft.contracts.view') === 'table' ? 'table' : 'cards'; } catch { return 'cards'; } });
  const pickView = (v: 'cards' | 'table') => { setView(v); try { localStorage.setItem('ft.contracts.view', v); } catch { /* egal */ } };
  const [toast] = useToast();
  const canEdit = canEditArea(me);
  useNewShortcut(useCallback(() => setCreating(true), []), canEdit);

  const f = { c: sp.get('kunde') ?? '', s: sp.get('status') ?? '', q: sp.get('q') ?? '', a: sp.get('bereich') ?? '' };
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const customers = [...new Set((data ?? []).map((c) => c.customer.name))].sort();
  const areas = [...new Set((data ?? []).flatMap((c) => c.areas.map((a) => a.area.name)))].sort();

  const list = useMemo(() => (data ?? []).filter((c) =>
    (!f.c || c.customer.name === f.c) &&
    (!f.a || c.areas.some((a) => a.area.name === f.a)) &&
    (!f.s || (f.s === 'notFiled' ? !c.filed : f.s === 'noType' ? !c.contractType : f.s === 'ending' ? c.endsInDays !== null && c.endsInDays >= 0 : c.state === f.s)) &&
    (!f.q || [c.name, c.contractNumber, c.customer.name, ...c.projects.map((p) => p.project.name)].join(' ').toLowerCase().includes(f.q.toLowerCase())))
    // zuerst, was bald endet; dann nach Budget
    .sort((a, b) => (a.endsInDays ?? 9999) - (b.endsInDays ?? 9999) || (b.budgetCents ?? 0) - (a.budgetCents ?? 0)), [data, f.c, f.s, f.q, f.a]);

  // Projekte ohne Hauptvertrag (Hinweis unten)
  const inContract = new Set((data ?? []).flatMap((c) => c.projects.map((p) => p.project.id)));
  const free = (lookups?.projects ?? []).filter((p) => !inContract.has(p.id) && (!f.c || lookups?.customers.find((x) => x.id === p.customerId)?.name === f.c));

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('ct.title')}</h1></div>
        <span className="spacer" />
        <SavedViews />
        {canEdit && <button className="btn primary" onClick={() => setCreating(true)} disabled={!lookups}>{t('ct.new')}</button>}
      </div>
      <p className="muted lead">{t('ct.lead')}</p>

      <div className="fbar">
        <div className="fld"><label htmlFor="ct-q">{t('common.search')}</label><input id="ct-q" type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('ct.searchPh')} /></div>
        <div className="fld"><label htmlFor="ct-c">{t('common.customer')}</label><select id="ct-c" value={f.c} onChange={(e) => set('kunde', e.target.value)}><option value="">{t('common.all')}</option>{customers.map((c) => <option key={c}>{c}</option>)}</select></div>
        <div className="fld"><label htmlFor="ct-s">{t('common.status')}</label>
          <select id="ct-s" value={f.s} onChange={(e) => set('status', e.target.value)}>
            <option value="">{t('common.all')}</option>
            {(['ok', 'nearlyUsed', 'exceeded', 'noBudget'] as const).map((s) => <option key={s} value={s}>{t(`ct.state.${s}`)}</option>)}
            <option value="ending">{t('ct.filterEnding')}</option>
            <option value="notFiled">{t('ct.filterNotFiled')}</option>
            <option value="noType">{t('ct.filterNoType')}</option>
          </select>
        </div>
        {areas.length > 1 && <div className="fld"><label htmlFor="ct-a">{t('ct.col.area')}</label><select id="ct-a" value={f.a} onChange={(e) => set('bereich', e.target.value)}><option value="">{t('common.all')}</option>{areas.map((a) => <option key={a}>{a}</option>)}</select></div>}
        {(f.c || f.s || f.q || f.a) && <button className="btn ghost" onClick={() => setSp(new URLSearchParams(), { replace: true })}>{t('common.reset')}</button>}
        <span className="spacer" />
        <div className="seg" role="group" aria-label={t('ct.view')}>
          <button aria-pressed={view === 'cards'} onClick={() => pickView('cards')}>{t('ct.view.cards')}</button>
          <button aria-pressed={view === 'table'} onClick={() => pickView('table')}>{t('ct.view.table')}</button>
        </div>
      </div>

      {data && data.length > 0 && <ContractKpis list={list} t={t} />}
      {data && data.some((c) => !c.contractType) && (() => { const xs = data.filter((c) => !c.contractType); return (
        <div className="banner info" role="status">
          <span aria-hidden="true">ⓘ</span>
          <div><b>{t(xs.length === data.length ? 'ct.typeBanner.all' : 'ct.typeBanner', { n: xs.length })}</b><div className="small muted">{t('ct.typeBanner.why')}</div></div>
          <span className="spacer" />
          <button className="btn" onClick={() => nav(`/vertraege/${xs[0]!.id}`)}>{t('ct.typeBanner.go')}</button>
        </div>); })()}

      {!data ? <Loading error={error} /> : !list.length ? <div className="box"><div className="empty">{data.length ? t('ct.emptyFilter') : t('ct.empty')}</div></div> : view === 'cards' ? (
        <div className="ctcards">{list.map((c) => <ContractCard key={c.id} c={c} t={t} />)}</div>
      ) : (
        <div className="box ctslim">
          <table>
            <colgroup><col style={{ width: '32%' }} /><col style={{ width: '10%' }} /><col style={{ width: '13%' }} /><col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '21%' }} /></colgroup>
            <thead><tr><th>{t('ct.col.contract')}</th><th>{t('cf.type')}</th><th>{t('ct.col.term')}</th><th className="r">{t('ct.col.budget')}</th><th className="r">{t('ct.col.rest')}</th><th>{t('ct.col.usage')} · {t('common.status')}</th></tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="click" tabIndex={0} onClick={() => nav(`/vertraege/${c.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') nav(`/vertraege/${c.id}`); }}>
                  <td className="ctname"><b>{c.name}</b><span>{c.customer.name} · {t('order.projectsN', { n: c.projects.length })}{c.contractNumber ? ` · ${c.contractNumber}` : ''}</span></td>
                  <td><TypePill c={c} t={t} /></td>
                  <td className="small num">{dfmt(c.validFrom)}<br />{dfmt(c.validTo)}</td>
                  <td className="r num">{c.budgetCents !== null ? eur(c.budgetCents) : '–'}</td>
                  <td className="r num" style={c.restCents !== null && c.restCents < 0 ? { color: 'var(--neg)' } : undefined}>{c.restCents !== null ? eur(c.restCents) : '–'}</td>
                  <td><UseBar c={c} /><div style={{ marginTop: 5 }}><ContractStatePill c={c} t={t} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lookups && data && free.length > 0 && (
        <div className="box">
          <div className="panel-h">
            <h2>{t('ct.freeTitle', { n: free.length })}</h2><span className="hint">{t('ct.freeHint')}</span><span className="spacer" />
            <button className="btn ghost" aria-expanded={showFree} onClick={() => setShowFree((v) => !v)}>{showFree ? t('sync.hide') : t('ct.freeShow')}</button>
          </div>
          {showFree && (
            <ul className="chips" style={{ padding: '0 16px 14px', margin: 0, listStyle: 'none' }}>
              {free.map((p) => (
                <li key={p.id}><Link className="chip" style={{ textDecoration: 'none' }} to={`/bestellungen?projekt=${encodeURIComponent(p.name)}`}>{lookups.customers.find((c) => c.id === p.customerId)?.name} · {p.name}</Link></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {creating && lookups && <ContractForm contract={null} lookups={lookups} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); nav(`/vertraege/${id}`); }} />}
      {toast}
    </section>
  );
}

const TYPE_TONE: Record<ContractType, string> = { frame: 'info', tm: 'crit', fixed: 'vio', flat: 'pos' };
export function TypePill({ c, t }: { c: Pick<ContractListItem, 'contractType' | 'billingCycle'>; t: T }) {
  if (!c.contractType) return <span className="pill neu">{t('ctype.none')}</span>;
  return <span className={`pill ${TYPE_TONE[c.contractType]}`} title={c.billingCycle ? t(`cf.cycle.${c.billingCycle}`) : undefined}>{t(`ctype.short.${c.contractType}`)}</span>;
}

/** Bestellt (hell) und abgerechnet (blau) im Verhältnis zum Budget (ohne Budget: zu Bestellt). */
export function UseBar({ c }: { c: ContractListItem }) {
  const base = Math.max(c.budgetCents ?? 0, c.orderedCents, 1);
  return (
    <div className="ctbar"><i className="o" style={{ width: `${(c.orderedCents / base) * 100}%`, background: c.restCents !== null && c.restCents < 0 ? 'var(--neg-soft)' : undefined }} /><i className="b" style={{ width: `${(c.billedCents / base) * 100}%` }} /></div>
  );
}

const custMark = (name: string) => name.replace(/\b(AG|GmbH|SE)\b/g, '').trim().slice(0, 3).toUpperCase();
export const termPos = (c: Pick<ContractListItem, 'validFrom' | 'validTo'>) => {
  if (!c.validFrom || !c.validTo) return null;
  const a = Date.parse(c.validFrom), b = Date.parse(c.validTo), n = Date.now();
  return b > a ? Math.max(0, Math.min(1, (n - a) / (b - a))) : null;
};

function ContractCard({ c, t }: { c: ContractListItem; t: T }) {
  const pos = termPos(c);
  return (
    <Link to={`/vertraege/${c.id}`} className="ctcard" aria-label={c.name}>
      <div className="ctop">
        <span className="ctmark" style={{ background: custColor(c.customer.name) }}>{custMark(c.customer.name)}</span>
        <div className="ttl"><b title={c.name}>{c.name}</b><span>{c.customer.name}{c.contractNumber ? ` · ${c.contractNumber}` : ''} · {t('order.projectsN', { n: c.projects.length })}{c.areas.length > 1 ? ` · ${c.areas.map((a) => a.area.name).join(', ')}` : ''}</span></div>
        {c.contractType && <TypePill c={c} t={t} />}
        <span className={`ctic${c.hasPdf ? ' on' : ' off'}`} title={c.hasPdf ? t('ct.pdf') : t('ct.missingPdf')}>PDF</span>
        <span className={`ctic${c.sharepointUrl ? ' on' : ' off'}`} title={c.sharepointUrl ? 'SharePoint' : t('ct.missingLink')}>SP</span>
      </div>
      <div className="ctvals">
        <div><small>{t('ct.col.budget')}</small><b className="num">{c.budgetCents !== null ? eur(c.budgetCents) : '–'}</b></div>
        <div><small>{t('ct.col.ordered')}</small><b className="num">{eur(c.orderedCents)}</b></div>
        <div><small>{t('ct.col.billed')}</small><b className="num">{eur(c.billedCents)}</b></div>
        <div><small>{t('ct.col.rest')}</small><b className="num" style={{ color: c.restCents === null ? 'var(--muted)' : c.restCents <= 0 ? 'var(--neg)' : 'var(--pos)' }}>{c.restCents !== null ? eur(c.restCents) : '–'}</b></div>
      </div>
      <div>
        <UseBar c={c} />
        <div className="ctlbl">{c.budgetCents ? t('ct.card.use', { o: pct(c.orderedCents / c.budgetCents), b: pct(c.billedCents / c.budgetCents) }) : t('ct.card.noBudget')}</div>
      </div>
      {pos !== null && (
        <div>
          <div className="cttime"><i style={{ width: `${pos * 100}%` }} /><span style={{ left: `${pos * 100}%` }} /></div>
          <div className="ctlbl three"><span>{dfmt(c.validFrom)}</span><span>{c.endsInDays !== null && c.endsInDays >= 0 ? <b className="crit">{t('ct.endsIn', { n: c.endsInDays })}</b> : t('ct.card.term', { p: pct(pos) })}</span><span>{dfmt(c.validTo)}</span></div>
        </div>
      )}
      {c.projects.length > 0 && (
        <div className="ctprojs">{c.projects.map((p) => <span key={p.project.id} className="ctpj" title={`${p.project.name}: ${eur(p.billedCents)} / ${eur(p.orderedCents)}`}><i style={{ ['--w' as string]: `${p.orderedCents ? Math.min(100, Math.round((p.billedCents / p.orderedCents) * 100)) : 0}%` }} />{p.project.name}</span>)}</div>
      )}
      <div className="ctfoot"><ContractStatePill c={c} t={t} /><span className="spacer" /><span className="small muted">{c.filed ? `✓ ${t('ct.filed')}` : t('ct.notFiled')}</span></div>
    </Link>
  );
}

function ContractKpis({ list, t }: { list: ContractListItem[]; t: T }) {
  const withBudget = list.filter((c) => c.budgetCents !== null);
  const sum = (xs: ContractListItem[], k: 'budgetCents' | 'orderedCents' | 'restCents') => xs.reduce((s, c) => s + (c[k] ?? 0), 0);
  const ending = list.filter((c) => c.endsInDays !== null && c.endsInDays >= 0).length;
  const noBudget = list.filter((c) => c.state === 'noBudget').length;
  const tight = list.filter((c) => c.state === 'nearlyUsed' || c.state === 'exceeded').length;
  const noType = list.filter((c) => !c.contractType).length;
  const mio = (c: number) => `${(c / 100_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Mio €`;
  return (
    <div className="ctkpis">
      <div className="ctk dark"><small>{t('ct.title')}</small><b>{list.length}</b><span>{t('ct.kpi.cust', { c: new Set(list.map((c) => c.customer.name)).size, p: list.reduce((s, c) => s + c.projects.length, 0) })}</span></div>
      <div className="ctk"><small>{t('ct.kpi.budget')}</small><b className="num">{mio(sum(withBudget, 'budgetCents'))}</b><span>{t('ct.kpi.withBudget', { n: withBudget.length, all: list.length })}</span></div>
      <div className="ctk"><small>{t('ct.col.ordered')}</small><b className="num">{mio(sum(list, 'orderedCents'))}</b><span>{t('ct.kpi.allContracts')}</span></div>
      <div className="ctk"><small>{t('ct.col.rest')}</small><b className="num pos">{mio(sum(withBudget, 'restCents'))}</b><span>{t('ct.kpi.inBudget')}</span></div>
      <div className="ctk"><small>{t('ct.kpi.attention')}</small><b>{ending + noBudget + tight + noType}</b><span className="pills">
        {ending > 0 && <span className="pill crit">{t('ct.kpi.ending', { n: ending })}</span>}
        {tight > 0 && <span className="pill crit">{t('ct.kpi.tight', { n: tight })}</span>}
        {noBudget > 0 && <span className="pill neu">{t('ct.kpi.noBudget', { n: noBudget })}</span>}
        {noType > 0 && <span className="pill neu">{t('ct.kpi.noType', { n: noType })}</span>}
      </span></div>
    </div>
  );
}
